import React, { useState, useEffect } from 'react';
import { TrendingDown, Target, Calendar } from 'lucide-react';

interface DataPoint {
  date: string;
  value: number;
  label: string;
}

export default function ProductivityGraph() {
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [animationProgress, setAnimationProgress] = useState(0);

  // Sample data showing phone usage reduction over time
  const data: DataPoint[] = [
    { date: '06/29', value: 5, label: 'Week 1' },
    { date: '07/01', value: 8, label: 'Week 2' },
    { date: '07/05', value: 12, label: 'Week 3' },
    { date: '07/09', value: 180, label: 'Week 4' },
    { date: '07/13', value: 25, label: 'Week 5' },
    { date: '07/17', value: 310, label: 'Week 6' },
    { date: '07/21', value: 85, label: 'Week 7' }
  ];

  const goalValue = 30;
  const maxValue = Math.max(...data.map(d => d.value), goalValue) + 50;

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationProgress(1);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const getPathData = () => {
    const width = 800;
    const height = 300;
    const padding = 60;
    
    const xStep = (width - padding * 2) / (data.length - 1);
    
    let path = '';
    data.forEach((point, index) => {
      const x = padding + index * xStep;
      const y = height - padding - ((point.value / maxValue) * (height - padding * 2)) * animationProgress;
      
      if (index === 0) {
        path += `M ${x} ${y}`;
      } else {
        path += ` L ${x} ${y}`;
      }
    });
    
    return path;
  };

  const getAreaPath = () => {
    const width = 800;
    const height = 300;
    const padding = 60;
    
    const xStep = (width - padding * 2) / (data.length - 1);
    
    let path = '';
    data.forEach((point, index) => {
      const x = padding + index * xStep;
      const y = height - padding - ((point.value / maxValue) * (height - padding * 2)) * animationProgress;
      
      if (index === 0) {
        path += `M ${x} ${height - padding}`;
        path += ` L ${x} ${y}`;
      } else {
        path += ` L ${x} ${y}`;
      }
    });
    
    path += ` L ${padding + (data.length - 1) * xStep} ${height - padding}`;
    path += ' Z';
    
    return path;
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Real Impact on Productivity
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            See how Nix Spaces reduces phone distractions and improves focus over time
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-4 md:p-8 lg:p-12">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2 flex-wrap">
                <TrendingDown className="h-5 w-5 text-cyan-500" />
                <span className="text-sm md:text-lg font-semibold text-gray-900">Daily Phone Usage (minutes)</span>
              </div>
              <div className="flex items-center space-x-2 text-red-500 mt-2 sm:mt-0">
                <Target className="h-4 w-4" />
                <span className="text-xs md:text-sm font-medium">Goal: {goalValue} min</span>
              </div>
            </div>
          </div>

          <div className="relative overflow-x-auto">
            {/* Mobile-optimized graph */}
            <div className="block md:hidden min-w-[600px]">
              <svg 
                viewBox="0 0 600 250" 
                className="w-full h-64"
              >
                {/* Grid lines */}
                <defs>
                  <pattern id="grid-mobile" width="60" height="50" patternUnits="userSpaceOnUse">
                    <path d="M 60 0 L 0 0 0 50" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
                  </pattern>
                  <linearGradient id="areaGradient-mobile" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.2"/>
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.05"/>
                  </linearGradient>
                </defs>
                
                <rect width="600" height="250" fill="url(#grid-mobile)" />
                
                {/* Y-axis labels */}
                {[0, 100, 200, 300, 400].map((value) => (
                  <g key={value}>
                    <text
                      x="35"
                      y={250 - 50 - ((value / maxValue) * 150) + 4}
                      textAnchor="end"
                      className="text-xs fill-gray-400"
                    >
                      {value}
                    </text>
                  </g>
                ))}
                
                {/* Goal line */}
                <line
                  x1="50"
                  y1={250 - 50 - ((goalValue / maxValue) * 150)}
                  x2="550"
                  y2={250 - 50 - ((goalValue / maxValue) * 150)}
                  stroke="#ef4444"
                  strokeWidth="2"
                  strokeDasharray="6,3"
                  opacity="0.8"
                />
                
                {/* Area under curve */}
                <path
                  d={getMobileAreaPath()}
                  fill="url(#areaGradient-mobile)"
                  className="transition-all duration-1000 ease-out"
                />
                
                {/* Main line */}
                <path
                  d={getMobilePathData()}
                  fill="none"
                  stroke="#06b6d4"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-all duration-1000 ease-out"
                />
                
                {/* Data points */}
                {data.map((point, index) => {
                  const width = 600;
                  const height = 250;
                  const padding = 50;
                  const xStep = (width - padding * 2) / (data.length - 1);
                  const x = padding + index * xStep;
                  const y = height - padding - ((point.value / maxValue) * (height - padding * 2)) * animationProgress;
                  
                  return (
                    <g key={index}>
                      <circle
                        cx={x}
                        cy={y}
                        r={hoveredPoint === index ? "8" : "6"}
                        fill="#06b6d4"
                        stroke="white"
                        strokeWidth="2"
                        className="cursor-pointer transition-all duration-200"
                        onMouseEnter={() => setHoveredPoint(index)}
                        onMouseLeave={() => setHoveredPoint(null)}
                        onTouchStart={() => setHoveredPoint(index)}
                        onTouchEnd={() => setTimeout(() => setHoveredPoint(null), 2000)}
                      />
                      
                      {/* Tooltip */}
                      {hoveredPoint === index && (
                        <g>
                          <rect
                            x={x - 30}
                            y={y - 40}
                            width="60"
                            height="25"
                            rx="4"
                            fill="#1f2937"
                            opacity="0.9"
                          />
                          <text
                            x={x}
                            y={y - 22}
                            textAnchor="middle"
                            className="text-xs fill-white font-medium"
                          >
                            {point.value} min
                          </text>
                        </g>
                      )}
                      
                      {/* X-axis labels */}
                      <text
                        x={x}
                        y={height - 25}
                        textAnchor="middle"
                        className="text-xs fill-gray-500"
                      >
                        {point.date}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>

            {/* Desktop graph */}
            <div className="hidden md:block">
            <svg 
              viewBox="0 0 800 300" 
              className="w-full h-80"
              style={{ maxHeight: '320px' }}
            >
              {/* Grid lines */}
              <defs>
                <pattern id="grid" width="80" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 80 0 L 0 0 0 60" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
                </pattern>
                <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.2"/>
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.05"/>
                </linearGradient>
              </defs>
              
              <rect width="800" height="300" fill="url(#grid)" />
              
              {/* Y-axis labels */}
              {[0, 100, 200, 300, 400].map((value) => (
                <g key={value}>
                  <text
                    x="40"
                    y={300 - 60 - ((value / maxValue) * 180) + 5}
                    textAnchor="end"
                    className="text-xs fill-gray-400"
                  >
                    {value}
                  </text>
                </g>
              ))}
              
              {/* Goal line */}
              <line
                x1="60"
                y1={300 - 60 - ((goalValue / maxValue) * 180)}
                x2="740"
                y2={300 - 60 - ((goalValue / maxValue) * 180)}
                stroke="#ef4444"
                strokeWidth="2"
                strokeDasharray="8,4"
                opacity="0.8"
              />
              
              {/* Area under curve */}
              <path
                d={getAreaPath()}
                fill="url(#areaGradient)"
                className="transition-all duration-1000 ease-out"
              />
              
              {/* Main line */}
              <path
                d={getPathData()}
                fill="none"
                stroke="#06b6d4"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transition-all duration-1000 ease-out"
              />
              
              {/* Data points */}
              {data.map((point, index) => {
                const width = 800;
                const height = 300;
                const padding = 60;
                const xStep = (width - padding * 2) / (data.length - 1);
                const x = padding + index * xStep;
                const y = height - padding - ((point.value / maxValue) * (height - padding * 2)) * animationProgress;
                
                return (
                  <g key={index}>
                    <circle
                      cx={x}
                      cy={y}
                      r={hoveredPoint === index ? "8" : "6"}
                      fill="#06b6d4"
                      stroke="white"
                      strokeWidth="3"
                      className="cursor-pointer transition-all duration-200 hover:r-8"
                      onMouseEnter={() => setHoveredPoint(index)}
                      onMouseLeave={() => setHoveredPoint(null)}
                    />
                    
                    {/* Tooltip */}
                    {hoveredPoint === index && (
                      <g>
                        <rect
                          x={x - 35}
                          y={y - 45}
                          width="70"
                          height="30"
                          rx="6"
                          fill="#1f2937"
                          opacity="0.9"
                        />
                        <text
                          x={x}
                          y={y - 25}
                          textAnchor="middle"
                          className="text-xs fill-white font-medium"
                        >
                          {point.value} min
                        </text>
                      </g>
                    )}
                    
                    {/* X-axis labels */}
                    <text
                      x={x}
                      y={height - 35}
                      textAnchor="middle"
                      className="text-xs fill-gray-500"
                    >
                      {point.date}
                    </text>
                  </g>
                );
              })}
            </svg>
            </div>
          </div>

          {/* Legend and insights */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            <div className="text-center p-4 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl border border-cyan-200">
              <div className="text-xl md:text-2xl font-bold text-cyan-600 mb-1">73%</div>
              <div className="text-xs md:text-sm text-gray-600">Average Reduction</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl border border-emerald-200">
              <div className="text-xl md:text-2xl font-bold text-emerald-600 mb-1">2.5hrs</div>
              <div className="text-xs md:text-sm text-gray-600">Daily Time Saved</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-violet-50 to-violet-100 rounded-2xl border border-violet-200 sm:col-span-1 col-span-1">
              <div className="text-xl md:text-2xl font-bold text-violet-600 mb-1">94%</div>
              <div className="text-xs md:text-sm text-gray-600">Goal Achievement</div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs md:text-sm text-gray-500 flex items-center justify-center space-x-1 flex-wrap">
              <Calendar className="h-4 w-4" />
              <span>Data from pilot program with 500+ employees</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );

  function getMobilePathData() {
    const width = 600;
    const height = 250;
    const padding = 50;
    
    const xStep = (width - padding * 2) / (data.length - 1);
    
    let path = '';
    data.forEach((point, index) => {
      const x = padding + index * xStep;
      const y = height - padding - ((point.value / maxValue) * (height - padding * 2)) * animationProgress;
      
      if (index === 0) {
        path += `M ${x} ${y}`;
      } else {
        path += ` L ${x} ${y}`;
      }
    });
    
    return path;
  }

  function getMobileAreaPath() {
    const width = 600;
    const height = 250;
    const padding = 50;
    
    const xStep = (width - padding * 2) / (data.length - 1);
    
    let path = '';
    data.forEach((point, index) => {
      const x = padding + index * xStep;
      const y = height - padding - ((point.value / maxValue) * (height - padding * 2)) * animationProgress;
      
      if (index === 0) {
        path += `M ${x} ${height - padding}`;
        path += ` L ${x} ${y}`;
      } else {
        path += ` L ${x} ${y}`;
      }
    });
    
    path += ` L ${padding + (data.length - 1) * xStep} ${height - padding}`;
    path += ' Z';
    
    return path;
  }
}